import { useEffect, useRef } from "react";

export default function CircuitBackground({ className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId;
    let width = (canvas.width = canvas.parentElement.offsetWidth);
    let height = (canvas.height = canvas.parentElement.offsetHeight);

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Handle Resize
    const handleResize = () => {
      if (!canvas.parentElement) return;
      width = canvas.width = canvas.parentElement.offsetWidth;
      height = canvas.height = canvas.parentElement.offsetHeight;
      initNetwork();
    };

    window.addEventListener("resize", handleResize);

    // Mouse tracking for subtle interactive pull
    let mouse = { x: -1000, y: -1000 };
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    window.addEventListener("mousemove", handleMouseMove);

    // Circuit Nodes and Traces
    let nodes = [];
    let pulses = [];

    const initNetwork = () => {
      nodes = [];
      pulses = [];

      const nodeCount = Math.floor((width * height) / 18000); // density
      const cols = Math.ceil(Math.sqrt(nodeCount * (width / height)));
      const rows = Math.ceil(nodeCount / cols);

      const cellW = width / cols;
      const cellH = height / rows;

      // Create grid-aligned circuit nodes with jitter
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          nodes.push({
            x: c * cellW + cellW * 0.5 + (Math.random() - 0.5) * cellW * 0.4,
            y: r * cellH + cellH * 0.5 + (Math.random() - 0.5) * cellH * 0.4,
            radius: Math.random() < 0.25 ? 2.8 : 1.6,
            connections: [],
            isHub: Math.random() < 0.15,
          });
        }
      }

      // Connect nodes horizontally / vertically / 45-degree angles (circuit layout)
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[j].x - nodes[i].x;
          const dy = nodes[j].y - nodes[i].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < cellW * 1.6) {
            nodes[i].connections.push({ target: nodes[j], dist });
          }
        }
      }
    };

    initNetwork();

    // Spawn pulses along connections
    let lastPulseTime = 0;
    const addPulse = () => {
      if (nodes.length === 0) return;
      const startNode = nodes[Math.floor(Math.random() * nodes.length)];
      if (startNode && startNode.connections.length > 0) {
        const conn = startNode.connections[Math.floor(Math.random() * startNode.connections.length)];
        pulses.push({
          start: startNode,
          end: conn.target,
          progress: 0,
          speed: 0.008 + Math.random() * 0.012,
          color: Math.random() < 0.6 ? "#7dd3fc" : "#0099cc",
          length: 0.25,
        });
      }
    };

    // Render loop
    const render = (time) => {
      ctx.clearRect(0, 0, width, height);

      // Spawn pulses periodically
      if (time - lastPulseTime > 180 && pulses.length < 25 && !prefersReducedMotion) {
        addPulse();
        lastPulseTime = time;
      }

      // Draw Circuit Lines
      ctx.lineWidth = 1;
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        for (let k = 0; k < n.connections.length; k++) {
          const target = n.connections[k].target;
          const dist = n.connections[k].dist;

          // Interactive mouse brightness
          const dMouse = Math.hypot(n.x - mouse.x, n.y - mouse.y);
          const mouseBoost = Math.max(0, 1 - dMouse / 180);

          ctx.strokeStyle = `rgba(0, 153, 204, ${0.08 + mouseBoost * 0.2})`;
          ctx.beginPath();
          ctx.moveTo(n.x, n.y);
          ctx.lineTo(target.x, target.y);
          ctx.stroke();
        }
      }

      // Draw Nodes
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        const dMouse = Math.hypot(n.x - mouse.x, n.y - mouse.y);
        const mouseBoost = Math.max(0, 1 - dMouse / 180);

        ctx.fillStyle = n.isHub
          ? `rgba(125, 211, 252, ${0.4 + mouseBoost * 0.5})`
          : `rgba(0, 102, 153, ${0.25 + mouseBoost * 0.4})`;

        ctx.beginPath();
        ctx.arc(n.x, n.y, n.radius + mouseBoost * 1.5, 0, Math.PI * 2);
        ctx.fill();

        if (n.isHub && !prefersReducedMotion) {
          ctx.strokeStyle = `rgba(125, 211, 252, ${0.2 + mouseBoost * 0.3})`;
          ctx.stroke();
        }
      }

      // Draw & Update Pulses (Electric Signal Waves)
      if (!prefersReducedMotion) {
        for (let p = pulses.length - 1; p >= 0; p--) {
          const pulse = pulses[p];
          pulse.progress += pulse.speed;

          if (pulse.progress >= 1) {
            pulses.splice(p, 1);
            continue;
          }

          const curX = pulse.start.x + (pulse.end.x - pulse.start.x) * pulse.progress;
          const curY = pulse.start.y + (pulse.end.y - pulse.start.y) * pulse.progress;

          const tailProgress = Math.max(0, pulse.progress - pulse.length);
          const tailX = pulse.start.x + (pulse.end.x - pulse.start.x) * tailProgress;
          const tailY = pulse.start.y + (pulse.end.y - pulse.start.y) * tailProgress;

          const grad = ctx.createLinearGradient(tailX, tailY, curX, curY);
          grad.addColorStop(0, "rgba(0, 153, 204, 0)");
          grad.addColorStop(1, pulse.color);

          ctx.strokeStyle = grad;
          ctx.lineWidth = 2.2;
          ctx.beginPath();
          ctx.moveTo(tailX, tailY);
          ctx.lineTo(curX, curY);
          ctx.stroke();

          // Glowing tip
          ctx.fillStyle = "#ffffff";
          ctx.beginPath();
          ctx.arc(curX, curY, 1.8, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      animationFrameId = requestAnimationFrame(render);
    };

    animationFrameId = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 pointer-events-none z-10 w-full h-full mix-blend-screen ${className}`}
    />
  );
}
